import {
  AddCircleOutlineRounded,
  DeleteOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";

import CustomDatePicker from "../CustomDatePicker";
import CustomDialog from "../CustomDialog";
import { STATUS_CHIP_CONFIG } from "./OrderFilterControls";
import type { OrderStatus } from "../../types/order";

const TRACKING_NO_MAX_LENGTH = 12;
const TRACKING_NO_REGEX = /^(RX|BL)\d{10}$/;
const TRACKING_NO_ERROR_MESSAGE =
  "Tracking No must start with 'RX' or 'BL' followed by 10 digits.";

const isTrackingValueValid = (value: string) =>
  TRACKING_NO_REGEX.test(value);

export interface OrderStatusUpdateDialogProps {
  open: boolean;
  title: string;
  subtitle?: string;
  status: OrderStatus;
  trackingNumbers: { id: string; value: string }[];
  trackingDate: string;
  details: string;
  isSubmitting: boolean;
  isSubmitDisabled?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  onClose: () => void;
  onStatusChange: (status: OrderStatus) => void;
  onTrackingNumbersChange: (trackingNumbers: { id: string; value: string }[]) => void;
  onTrackingDateChange: (trackingDate: string) => void;
  onSubmit: () => void;
}

export default function OrderStatusUpdateDialog({
  open,
  title,
  subtitle = "Please review carefully before updating the status.",
  status,
  trackingNumbers,
  trackingDate,
  details,
  isSubmitting,
  isSubmitDisabled: isSubmitDisabledProp = false,
  submitLabel = "Update Status",
  cancelLabel = "Discard",
  onClose,
  onStatusChange,
  onTrackingNumbersChange,
  onTrackingDateChange,
  onSubmit,
}: OrderStatusUpdateDialogProps) {
  const isCreated = status === "CREATED";

  const nonEmptyTrackingNumbers = trackingNumbers.filter(
    (t) => t.value.trim().length > 0,
  );
  const hasTrackingNumber = nonEmptyTrackingNumbers.length > 0;
  const allTrackingNumbersValid = nonEmptyTrackingNumbers.every((t) =>
    isTrackingValueValid(t.value),
  );

  const isSubmitDisabled = isCreated
    ? isSubmitting ||
      isSubmitDisabledProp ||
      !hasTrackingNumber ||
      !allTrackingNumbersValid ||
      !trackingDate
    : isSubmitting || isSubmitDisabledProp;

  const handleAddTracking = () => {
    onTrackingNumbersChange([
      ...trackingNumbers,
      { id: Math.random().toString(36).substring(2, 9), value: "" },
    ]);
  };

  const handleRemoveTracking = (id: string) => {
    onTrackingNumbersChange(trackingNumbers.filter((t) => t.id !== id));
  };

  const handleTrackingChange = (id: string, value: string) => {
    onTrackingNumbersChange(
      trackingNumbers.map((t) => (t.id === id ? { ...t, value } : t)),
    );
  };

  return (
    <CustomDialog
      open={open}
      title={title}
      onClose={onClose}
      maxWidth="sm"
      content={
        <Stack spacing={2.5} sx={{ pt: 0.5, width: "100%" }}>
          <Typography sx={{ fontSize: 16, color: "#8E95A2", mt: -0.5 }}>
            {subtitle}
          </Typography>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 16, fontWeight: 400 }}>
              Select Status
              <Box component="span" sx={{ color: "#BE2217" }}>
                *
              </Box>
            </Typography>
            <TextField
              select
              fullWidth
              value={status}
              onChange={(e) => {
                const newStatus = e.target.value as OrderStatus;
                onStatusChange(newStatus);
                onTrackingDateChange("");
                onTrackingNumbersChange([
                  { id: Math.random().toString(36).substring(2, 9), value: "" },
                ]);
              }}
              slotProps={{
                input: { sx: { py: 0.25 } },
              }}
              SelectProps={{
                renderValue: (value) => {
                  const config = STATUS_CHIP_CONFIG[value as string];
                  if (!config) return String(value);
                  return (
                    <Chip
                      label={config.label}
                      size="small"
                      sx={{
                        height: 32,
                        borderRadius: 1,
                        backgroundColor: config.bg,
                        color: config.color,
                        fontSize: 12,
                        fontWeight: 400,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  );
                },
              }}
            >
              {Object.entries(STATUS_CHIP_CONFIG).map(([value, config]) => (
                <MenuItem key={value} value={value}>
                  <Chip
                    label={config.label}
                    size="small"
                    sx={{
                      height: 24,
                      borderRadius: "4px",
                      backgroundColor: config.bg,
                      color: config.color,
                      fontSize: 12,
                      fontWeight: 400,
                      "& .MuiChip-label": { px: "6px" },
                    }}
                  />
                </MenuItem>
              ))}
            </TextField>

          </Stack>

          {isCreated && (
            <Stack spacing={1.25}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1.5}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  Tracking No. Detail
                </Typography>
                <Button
                  onClick={handleAddTracking}
                  variant="contained"
                  startIcon={<AddCircleOutlineRounded />}
                  sx={{
                    height: 32,
                    borderRadius: "96px",
                    bgcolor: "#111111",
                    color: "#FFFFFF",
                    textTransform: "none",
                    fontSize: 14,
                    fontWeight: 600,
                    px: 2,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#2A2A2A", boxShadow: "none" },
                  }}
                >
                  Add Tracking No.
                </Button>
              </Stack>
              {trackingNumbers.map((tracking) => (
                <Stack key={tracking.id} spacing={1}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      Tracking No.
                      <Box component="span" sx={{ color: "#BE2217" }}>
                        *
                      </Box>
                    </Typography>
                    {trackingNumbers.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveTracking(tracking.id)}
                        sx={{ color: "#BE2217" }}
                      >
                        <DeleteOutlined fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                  <TextField
                    size="small"
                    placeholder="Tracking No."
                    value={tracking.value}
                    onChange={(e) =>
                      handleTrackingChange(tracking.id, e.target.value)
                    }
                    fullWidth
                    error={
                      tracking.value.trim().length > 0 &&
                      !isTrackingValueValid(tracking.value)
                    }
                    helperText={
                      tracking.value.trim().length > 0 &&
                      !isTrackingValueValid(tracking.value)
                        ? TRACKING_NO_ERROR_MESSAGE
                        : undefined
                    }
                    slotProps={{
                      htmlInput: {
                        maxLength: TRACKING_NO_MAX_LENGTH,
                      },
                    }}
                  />
                </Stack>
              ))}

              <Stack spacing={1}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  Shipping Date
                  <Box component="span" sx={{ color: "#BE2217" }}>
                    *
                  </Box>
                </Typography>
                <CustomDatePicker
                  size="small"
                  fullWidth
                  format="DD/MM/YYYY"
                  value={trackingDate ? dayjs(trackingDate) : null}
                  onChange={(date: dayjs.Dayjs | null) =>
                    onTrackingDateChange(date ? date.format("YYYY-MM-DD") : "")
                  }
                  slotProps={{
                    textField: {
                      placeholder: "Select Tracking Date",
                    },
                  }}
                />
              </Stack>
            </Stack>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isSubmitting}
              sx={{
                height: 40,
                minWidth: 109,
                borderRadius: "96px",
                borderColor: "#111111",
                color: "#111111",
                fontSize: 16,
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { borderColor: "#111111" },
              }}
            >
              {cancelLabel}
            </Button>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={isSubmitDisabled}
              sx={{
                height: 40,
                minWidth: 169,
                borderRadius: "96px",
                bgcolor: "#111111",
                color: "#FFFFFF",
                fontSize: 16,
                fontWeight: 700,
                textTransform: "none",
                boxShadow:
                  "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
                "&:hover": { bgcolor: "#2A2A2A" },
              }}
            >
              {submitLabel}
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
}
